import { SwapOutlined } from '@ant-design/icons';
import { Form, InputNumber } from 'antd';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';

import styles from '../ExperienceDiscount.module.scss';

import { RegularPrice } from './RegularPrice';
import { type ExperiencePrices, roundToOneDecimal } from './experiencePricing';

type PriceInputRowProps = {
  originalPrice: number;
  discountedPrice: number;
  hasDiscount: boolean;
  label: string;
  disabled: boolean;
  onChange: (value: number | null) => void;
};

const PriceInputRow = ({
  originalPrice,
  discountedPrice,
  hasDiscount,
  label,
  disabled,
  onChange,
}: PriceInputRowProps) => {
  const { currencySymbol } = useContext(CurrencyContext);

  return (
    <div className={styles.priceInputRow}>
      <RegularPrice
        originalPrice={originalPrice}
        discountedPrice={discountedPrice}
        hasDiscount={hasDiscount}
        label={label}
      />
      <InputNumber
        min={0}
        max={originalPrice}
        precision={1}
        addonAfter={currencySymbol || '€'}
        value={discountedPrice}
        disabled={disabled}
        onChange={onChange}
      />
    </div>
  );
};

type DiscountFieldsProps = {
  prices: ExperiencePrices;
  discount: number;
  adultDiscountedPrice: number;
  childDiscountedPrice?: number;
  discountFieldName: string;
  hasError: boolean;
  onAdultPriceChange: (value: number | null) => void;
  onChildPriceChange: (value: number | null) => void;
  onDiscountBlur: () => void;
};

export const DiscountFields = ({
  prices,
  discount,
  adultDiscountedPrice,
  childDiscountedPrice,
  discountFieldName,
  hasError,
  onAdultPriceChange,
  onChildPriceChange,
  onDiscountBlur,
}: DiscountFieldsProps) => {
  const hasDiscount = discount > 0;
  const { childPrice } = prices;

  const hasChild =
    childPrice !== undefined && childDiscountedPrice !== undefined;

  const isPriceEditable = !prices.isVariablePrice && !hasChild;

  return (
    <div className={styles.discountFields}>
      <div className={styles.priceInputs}>
        <PriceInputRow
          originalPrice={prices.adultPrice}
          discountedPrice={adultDiscountedPrice}
          hasDiscount={hasDiscount}
          label={hasChild ? ' /adult' : ' /unit'}
          disabled={!isPriceEditable}
          onChange={onAdultPriceChange}
        />
        {hasChild && (
          <PriceInputRow
            originalPrice={childPrice}
            discountedPrice={childDiscountedPrice}
            hasDiscount={hasDiscount}
            label=" /child"
            disabled={!isPriceEditable}
            onChange={onChildPriceChange}
          />
        )}
      </div>

      <SwapOutlined className={styles.swapIcon} />

      <Form.Item
        noStyle
        name={discountFieldName}
        normalize={(value) =>
          value === null || value === undefined
            ? value
            : roundToOneDecimal(value)
        }
        rules={[{ required: true, message: 'Required' }]}
      >
        <InputNumber
          min={0}
          max={100}
          precision={1}
          addonAfter="%"
          onBlur={onDiscountBlur}
          status={hasError ? 'error' : ''}
        />
      </Form.Item>
    </div>
  );
};
